import {
  EntregaDeTrabalhoFormato,
  IEntregaDeTrabalho,
  IEntregaDeTrabalhoComplete,
  IEntregaDeTrabalhoFormPayload,
  IEntregaDeTrabalhoOption,
  IEntregaDeTrabalhoReview,
  IListFilters,
  IReviewTimeStatistics,
  IStatistics
} from '../../pages/Dashboard/EntregaTrabalho/models'
import apiService from '../Api'

interface IList {
  draw: number
  start: number
  length: number
  order: {
    column: number
    dir: string
  }[]
  filters: IListFilters
}

export class EntregaDeTrabalhoService {
  async list(params: IList): Promise<{ data: IEntregaDeTrabalho[]; recordsFiltered: number }> {
    const queryParams = new URLSearchParams()
    queryParams.append('draw', params.draw.toString())
    queryParams.append('start', params.start.toString())
    queryParams.append('length', params.length.toString())
    queryParams.append('order[0][column]', params.order[0].column.toString())
    queryParams.append('order[0][dir]', params.order[0].dir)
    queryParams.append('search[value]', params.filters.search)
    queryParams.append('teacher', params.filters.teacher)
    queryParams.append('type', params.filters.type)
    queryParams.append('course[format]', params.filters.course.format)
    queryParams.append('course[name]', params.filters.course.name)
    queryParams.append('created_at[from]', params.filters.created_at.from)
    queryParams.append('created_at[to]', params.filters.created_at.to)
    queryParams.append('status', params.filters.status)
    queryParams.append('is_validated_by_secretary', params.filters.is_validated_by_secretary)

    return apiService.get('/entrega-de-trabalho', { params: queryParams }).then((response) => response.data)
  }

  async loadStatistics(filters: IListFilters): Promise<IStatistics> {
    const queryParams = new URLSearchParams()
    queryParams.append('search[value]', filters.search)
    queryParams.append('teacher', filters.teacher)
    queryParams.append('type', filters.type)
    queryParams.append('course[format]', filters.course.format)
    queryParams.append('course[name]', filters.course.name)
    queryParams.append('created_at[from]', filters.created_at.from)
    queryParams.append('created_at[to]', filters.created_at.to)
    queryParams.append('status', filters.status)

    return apiService
      .get('/entrega-de-trabalho/get-statistics', { params: queryParams })
      .then((response) => response.data)
  }

  async loadReviewTimeStatistics(filters: IListFilters): Promise<IReviewTimeStatistics> {
    const queryParams = new URLSearchParams()
    queryParams.append('search[value]', filters.search)
    queryParams.append('teacher', filters.teacher)
    queryParams.append('type', filters.type)
    queryParams.append('course[format]', filters.course.format)
    queryParams.append('course[name]', filters.course.name)
    queryParams.append('created_at[from]', filters.created_at.from)
    queryParams.append('created_at[to]', filters.created_at.to)
    queryParams.append('status', filters.status)

    return apiService
      .get('/entrega-de-trabalho/get-review-time-statistics', { params: queryParams })
      .then((response) => response.data)
  }

  async get(id: number): Promise<IEntregaDeTrabalhoComplete> {
    return apiService.get(`/entrega-de-trabalho/${id}`).then((response) => response.data)
  }

  async getCourses(): Promise<{ name: string; format: string }[]> {
    return apiService.get('/entrega-de-trabalho/courses').then((response) => response.data)
  }

  async getTeachers(): Promise<string[]> {
    return apiService.get('/entrega-de-trabalho/teachers').then((response) => response.data)
  }

  /** Professores marcados como "Exibir nos forms" (formulário público). */
  async getFormTeachers(): Promise<IEntregaDeTrabalhoOption[]> {
    return apiService.get('/entrega-de-trabalho/form/teachers').then((response) => response.data)
  }

  /** Cursos do formato escolhido (formulário público). */
  async getFormCourses(format: EntregaDeTrabalhoFormato): Promise<IEntregaDeTrabalhoOption[]> {
    return apiService.get('/entrega-de-trabalho/form/courses', { params: { format } }).then((response) => response.data)
  }

  /** Cria a entrega pelo formulário público e devolve o public_id para os uploads. */
  async submitForm(payload: IEntregaDeTrabalhoFormPayload): Promise<string> {
    return apiService.post('/entrega-de-trabalho/form', payload).then((response) => response.data.id)
  }

  /** Envia um arquivo da entrega recém-criada (rota pública, um arquivo por requisição). */
  async uploadPublicFile(publicId: string, file: File): Promise<void> {
    const formData = new FormData()
    formData.append('file', file)

    await apiService.post(`/entrega-de-trabalho/${publicId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  async delete(id: number): Promise<void> {
    return apiService.delete(`/entrega-de-trabalho/${id}`)
  }

  async deleteMultiple(ids: number[]): Promise<void> {
    return apiService.delete('/entrega-de-trabalho', { data: { ids } })
  }

  async getByPublicId(publicId: string): Promise<IEntregaDeTrabalhoComplete> {
    return apiService
      .get(`/entrega-de-trabalho/public/${encodeURIComponent(publicId)}`)
      .then((response) => response.data)
  }

  async updateStatus(id: number, status: string): Promise<void> {
    return apiService.put(`/entrega-de-trabalho/${id}/set-status`, { status })
  }

  async review(publicId: string, review: IEntregaDeTrabalhoReview): Promise<void> {
    await apiService.post(`/entrega-de-trabalho/review/${encodeURIComponent(publicId)}`, review)
  }

  async uploadFile(id: number, file: File): Promise<void> {
    const formData = new FormData()
    formData.append('file', file)

    return apiService
      .post(`/entrega-de-trabalho/${id}/uploadFile`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then((response) => response.data)
  }

  async deleteFile(id: number): Promise<void> {
    return apiService.delete(`/entrega-de-trabalho/${id}/uploadFile`)
  }

  async secretaryApprove(id: number): Promise<void> {
    return apiService.put(`/entrega-de-trabalho/${id}/secretaryApprove`)
  }

  async secretaryApproveMultiple(ids: number[]): Promise<void> {
    return apiService.put(`/entrega-de-trabalho/secretaryApproveMultiple`, { ids })
  }

  async secretaryDisapprove(id: number): Promise<void> {
    return apiService.put(`/entrega-de-trabalho/${id}/secretaryDisapprove`)
  }

  async secretaryDisapproveMultiple(ids: number[]): Promise<void> {
    return apiService.put(`/entrega-de-trabalho/secretaryDisapproveMultiple`, { ids })
  }
}
